import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import {
  Alert,
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form1120 = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="Form 7004 Extension Filing Guide for Form 1120 Filers"
          description="Explore our guide to filing a Form 7004 extension for Form 1120. Learn about Forms 1120, 1120-C, and 1120-S to ensure accurate submissions."
          keywords=""
          canonical="resources/tax-extension/form-7004-for-form-1120/"
          ogType="article"
          jsonLdPricing="17.99"
          jsonLdAggregateRatingValue="4.95"
          jsonLdAggregateRatingCount="10859"
        />
      }
    >
      <Container maxWidth="lg">
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 5,
            mt: 5,
            textAlign: 'center',
          }}
        >
          <span className="highlight-container">
            <span className="highlight">Form 7004 for Form 1120:</span>
          </span>{' '}
          Corporate Tax Extension Guide
        </Typography>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/tax-extension/form-7004-for-form-1120/"
                  />

                  {/* Article requires an image */}
                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>
                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-08-20T09:00:00-05:00"
                      >
                        Aug 20, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-08-20T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        Aug 20, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{
                            color: 'inherit',
                            textDecoration: 'none',
                          }}
                        >
                          Peter Samuel
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>
                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form 7004 for 1120 at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemProp="articleBody"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',
                      '& li': { mb: 1.6, position: 'relative', pl: 3 },
                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li>
                      <span>
                        <span style={{ fontWeight: 600 }}>Form 1120</span> is
                        used by C corporations to report income, deductions,
                        credits, gains, losses, and tax liability to the IRS,
                        while{' '}
                        <span style={{ fontWeight: 600 }}>Forms 1120-C</span>{' '}
                        and{' '}
                        <a
                          href="https://www.taxzerone.com/resources/business-tax/form-1120-s-instructions/"
                          style={{
                            color: '#0202ff',
                            textDecoration: 'underline',
                          }}
                        >
                          1120-S
                        </a>{' '}
                        are used by cooperatives and S corporations,
                        respectively.
                      </span>
                    </li>
                    <li>
                      <span>
                        <span style={{ fontWeight: 600 }}>
                          Corporations and other eligible business entities
                        </span>{' '}
                        can{' '}
                        <a
                          href="https://www.taxzerone.com/tax-extension/efile-irs-form-7004-business-tax-extension-online/"
                          style={{
                            color: '#0202ff',
                            textDecoration: 'underline',
                          }}
                        >
                          file Form 7004
                        </a>{' '}
                        to request an{' '}
                        <span style={{ fontWeight: 600 }}>
                          automatic extension of up to 6 months
                        </span>{' '}
                        to file eligible business income tax returns. The
                        extension applies only to filing the return,{' '}
                        <span style={{ fontWeight: 600 }}>
                          not to paying taxes owed
                        </span>
                        .
                      </span>
                    </li>
                    <li>
                      <span>
                        <span style={{ fontWeight: 600 }}>Businesses</span>{' '}
                        should submit{' '}
                        <span style={{ fontWeight: 600 }}>Form 7004</span> by
                        the original due date of the applicable tax return to
                        receive the automatic extension and avoid late-filing
                        penalties.
                      </span>
                    </li>
                  </Box>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {' '}
                  Corporations must understand the differences between tax forms
                  to stay compliant and maximize their tax strategies in the
                  complicated landscape of corporate taxation. In the US, Forms
                  1120, 1120-C, and{' '}
                  <Link
                    href="/resources/business-tax/form-1120-s-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    1120-S
                  </Link>{' '}
                  are among the most important forms used for company tax
                  filings. TaxZerone helps to examine the variations and
                  parallels between these forms along with{' '}
                  <Link
                    href="/resources/tax-extension/form-7004-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    Form 7004
                  </Link>{' '}
                  from this resource page to assist businesses in making
                  justifiable decisions about their tax liabilities.
                </Typography>
                <Box
                  sx={{
                    pt: 2,
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    <b>Table of Contents</b>
                  </Typography>
                  <List
                    sx={{ mb: 5, border: '1px solid #ccc', borderRadius: 2 }}
                  >
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#form1120" sx={{ color: '#0202ff' }}>
                          What is Form 1120?
                        </Link>
                        <List sx={{ pb: 0 }}>
                          <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                            <ListItemIcon
                              sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                            >
                              <DoubleArrowIcon />
                            </ListItemIcon>
                            <ListItemText>
                              <Link
                                href="#keyform1120"
                                sx={{ color: '#0202ff' }}
                              >
                                Key Features of Form 1120
                              </Link>
                            </ListItemText>
                          </ListItem>
                          <ListItem
                            sx={{ py: 0.5, pb: 0, alignItems: 'flex-start' }}
                          >
                            <ListItemIcon
                              sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                            >
                              <DoubleArrowIcon />
                            </ListItemIcon>
                            <ListItemText>
                              <Link href="#imporform" sx={{ color: '#0202ff' }}>
                                Important Considerations for Form 1120 Filers
                              </Link>
                            </ListItemText>
                          </ListItem>
                        </List>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#form1120c" sx={{ color: '#0202ff' }}>
                          What is Form 1120-C?
                        </Link>
                        <List sx={{ pb: 0 }}>
                          <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                            <ListItemIcon
                              sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                            >
                              <DoubleArrowIcon />
                            </ListItemIcon>
                            <ListItemText>
                              <Link
                                href="#keyform1120-c"
                                sx={{ color: '#0202ff' }}
                              >
                                Key Features of Form 1120-C
                              </Link>
                            </ListItemText>
                          </ListItem>
                          <ListItem
                            sx={{ py: 0.5, pb: 0, alignItems: 'flex-start' }}
                          >
                            <ListItemIcon
                              sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                            >
                              <DoubleArrowIcon />
                            </ListItemIcon>
                            <ListItemText>
                              <Link
                                href="#whyform1120c"
                                sx={{ color: '#0202ff' }}
                              >
                                Why Use Form 1120-C?
                              </Link>
                            </ListItemText>
                          </ListItem>
                        </List>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#form1120s" sx={{ color: '#0202ff' }}>
                          What is Form 1120-S?
                        </Link>
                        <List>
                          <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                            <ListItemIcon
                              sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                            >
                              <DoubleArrowIcon />
                            </ListItemIcon>
                            <ListItemText>
                              <Link
                                href="#keyform1120-s"
                                sx={{ color: '#0202ff' }}
                              >
                                Key Features of Form 1120-S
                              </Link>
                            </ListItemText>
                          </ListItem>
                        </List>
                      </ListItemText>
                    </ListItem>
                    <ListItem
                      sx={{ py: 0.5, alignItems: 'flex-start', mt: -2 }}
                    >
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#dbfrom1120" sx={{ color: '#0202ff' }}>
                          Difference between Form 1120, 1120-c, 1120-s
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#what" sx={{ color: '#0202ff' }}>
                          What is Form 7004?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#who" sx={{ color: '#0202ff' }}>
                          Who can file Form 7004?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#whereform7004" sx={{ color: '#0202ff' }}>
                          Where to file Form 7004?
                        </Link>
                      </ListItemText>
                    </ListItem>
                  </List>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="form1120"
                >
                  What is Form 1120?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <Link
                    href="/resources/business-tax/form-1120-s-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    Form 1120
                  </Link>
                  , officially titled &quot;U.S. Corporation Income Tax
                  Return,&quot; is the tax form used by regular C corporations
                  to report their income, deductions, gains, losses, credits,
                  and tax liabilities to the Internal Revenue Service (IRS).
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                    scrollMarginTop: 84,
                  }}
                  id="keyform1120"
                >
                  Key Features of Form 1120:
                </Typography>
                <Typography variant="body1" component="div" sx={{ mb: 4 }}>
                  <ul>
                    <li style={{ marginBottom: 16 }}>
                      Provides a thorough rundown of the financial operations of
                      the company during the tax year.
                    </li>
                    <li style={{ marginBottom: 16 }}>
                      Determine the amount of tax due to the IRS and the
                      corporation&apos;s taxable income.
                    </li>
                    <li style={{ marginBottom: 16 }}>
                      Enables businesses to lower their tax obligations by
                      claiming credits, deductions, and other tax benefits.
                    </li>
                  </ul>
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                    scrollMarginTop: 88,
                  }}
                  id="imporform"
                >
                  Important Considerations for Form 1120 Filers:
                </Typography>
                <Typography variant="body1" component="div" sx={{ mb: 4 }}>
                  <ul>
                    <li style={{ marginBottom: 16 }}>
                      <b>Deadline for Filing: </b>
                      Every year, Form 1120 must be filed by the fifteenth day
                      of the fourth month after the corporation&apos;s tax year
                      ends. Generally, <b>April 15th</b> is the deadline for
                      taxpayers filing for the calendar year.
                    </li>
                    <li style={{ marginBottom: 16 }}>
                      <b>Options for Extensions: </b>
                      Corporations may use Form 7004 to obtain an automatic
                      extension, which gives them an extra six months after the
                      initial due date if more time is needed to file Form 1120.
                    </li>
                    <li style={{ marginBottom: 16 }}>
                      <b>Electronic Filing: </b>
                      Corporations have the option to electronically file Form
                      1120, which offers benefits such as faster processing,
                      confirmation of receipt, and reduced errors.
                    </li>
                  </ul>
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="form1120c"
                >
                  What is Form 1120-C?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Form 1120-C, officially titled &quot;U.S. Income Tax Return
                  for Cooperative Associations, &quot; is used by cooperatives
                  to report their income, deductions, credits, and tax
                  liabilities to the Internal Revenue Service (IRS).
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                    scrollMarginTop: 84,
                  }}
                  id="keyform1120-c"
                >
                  Key Features of Form 1120-C:
                </Typography>
                <Typography variant="body1" component="div" sx={{ mb: 4 }}>
                  <ul>
                    <li style={{ marginBottom: 16 }}>
                      Designed especially for associations that work together.
                    </li>
                    <li style={{ marginBottom: 16 }}>
                      Permits cooperatives to file their taxable income reports
                      and to request credits and deductions related to their
                      business operations.
                    </li>
                    <li style={{ marginBottom: 16 }}>
                      Assists cooperatives in meeting their tax obligations and
                      complying with federal tax regulations.
                    </li>
                  </ul>
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                    scrollMarginTop: 88,
                  }}
                  id="whyform1120c"
                >
                  Why Use Form 1120-C?
                </Typography>
                <Typography variant="body1" component="div" sx={{ mb: 4 }}>
                  <ul>
                    <li style={{ marginBottom: 16 }}>
                      <b>Customized Reporting: </b>
                      To ensure accurate and compliant tax filing, Form 1120-C
                      provides cooperatives with unique reporting requirements
                      and tax treatment.
                    </li>
                    <li style={{ marginBottom: 16 }}>
                      <b>Specialized Deductions and Credits: </b>
                      To maximize tax savings, this form offers deductions and
                      credits that are particularly significant to cooperative
                      activities.
                    </li>
                    <li style={{ marginBottom: 16 }}>
                      <b>IRS Compliance: </b>
                      Cooperatives can comply with IRS regulations and avoid
                      fines for inaccurate or late tax reporting by filing Form
                      1120-C.
                    </li>
                    <li style={{ marginBottom: 16 }}>
                      <b>Deadline: </b>
                      Cooperatives usually file Form 1120-C by <b>April 15th</b>
                      , which is the 15th day of the 4th month after their tax
                      year ends.
                    </li>
                  </ul>
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="form1120s"
                >
                  What is Form 1120-S?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Form 1120-S, officially titled &quot;U.S. Income Tax Return
                  for an S Corporation,&quot; is used by S corporations to
                  report their income, deductions, credits, and other
                  tax-related information to the Internal Revenue Service (IRS).
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                    scrollMarginTop: 84,
                  }}
                  id="keyform1120-s"
                >
                  Key Features of Form 1120-S:
                </Typography>
                <Typography variant="body1" component="div" sx={{ mb: 4 }}>
                  <ul>
                    <li style={{ marginBottom: 16 }}>
                      <b>Pass-through taxation: </b>S corporations allow their
                      shareholders to report income, losses, credits, and
                      deductions on their tax returns.
                    </li>
                    <li style={{ marginBottom: 16 }}>
                      <b>Limited liability: </b>
                      Just like with C corporations, S corporation&apos;s
                      shareholders are shielded from certain types of lawsuits.
                    </li>
                    <li style={{ marginBottom: 16 }}>
                      <b>Directions and Advice: </b>
                      To assist you in correctly preparing and filing your tax
                      return, the IRS website provides detailed instructions for
                      filling out Form 1120-S.
                    </li>
                    <li style={{ marginBottom: 16 }}>
                      <b>Schedules and Tax Forms: </b>
                      Get access to all the forms, schedules, and attachments
                      needed to{' '}
                      <Link
                        href="/business-tax/efile-irs-form-1120-s/"
                        sx={{ color: '#0202ff' }}
                      >
                        file Form 1120-S
                      </Link>
                      , including the shareholders&apos;{' '}
                      <Link
                        href="/resources/business-tax/schedule-k-1/"
                        sx={{ color: '#0202ff' }}
                      >
                        {' '}
                        Schedule K-1
                      </Link>
                      .
                    </li>
                    <li style={{ marginBottom: 16 }}>
                      <b>Deadline: </b>
                      The deadline for Form 1120-S, used by S corporations, is
                      usually <b>March 16th</b>, falling on the 15th day of the
                      third month following the end of the corporation&apos;s
                      tax year.
                    </li>
                  </ul>
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="dbfrom1120"
                >
                  Difference between Form 1120, 1120-c, 1120-s
                </Typography>
                <Box component="div" sx={{ mb: 2 }}>
                  <TableContainer
                    sx={{
                      border: '1px solid #828282',
                      borderCollapse: 'collapse',
                    }}
                  >
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow
                          sx={{
                            backgroundColor: '#ccc',
                          }}
                        >
                          <TableCell
                            sx={{
                              color: '#444',
                              width: 300,
                              borderRight: '1px solid #828282',
                              textAlign: 'center',
                              borderBottom: '1px solid #828282',
                              fontWeight: 'bold',
                            }}
                          >
                            Aspect
                          </TableCell>

                          <TableCell
                            sx={{
                              color: '#444',
                              width: 300,
                              textAlign: 'center',
                              borderBottom: '1px solid #828282',
                              borderRight: '1px solid #828282',
                              fontWeight: 'bold',
                            }}
                          >
                            Form 1120
                          </TableCell>
                          <TableCell
                            sx={{
                              color: '#444',
                              width: 300,
                              textAlign: 'center',
                              borderBottom: '1px solid #828282',
                              borderRight: '1px solid #828282',
                              fontWeight: 'bold',
                            }}
                          >
                            Form 1120-C
                          </TableCell>
                          <TableCell
                            sx={{
                              color: '#444',
                              width: 300,
                              textAlign: 'center',
                              borderBottom: '1px solid #828282',
                              fontWeight: 'bold',
                            }}
                          >
                            Form 1120-S
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow
                          sx={{
                            textAlign: 'center',
                          }}
                        >
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                              verticalAlign: 'center',
                              textAlign: 'center',
                              fontWeight: 'bold',
                            }}
                          >
                            Entity Type
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                              verticalAlign: 'center',
                              textAlign: 'center',
                            }}
                          >
                            Regular C Corporation
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                              verticalAlign: 'center',
                              textAlign: 'center',
                            }}
                          >
                            Cooperative
                          </TableCell>
                          <TableCell
                            sx={{
                              borderBottom: '1px solid #828282',
                              verticalAlign: 'center',
                              textAlign: 'center',
                            }}
                          >
                            S Corporation
                          </TableCell>
                        </TableRow>
                        <TableRow
                          sx={{
                            textAlign: 'center',
                            backgroundColor: '#f2f2f2',
                          }}
                        >
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                              verticalAlign: 'center',
                              textAlign: 'center',
                              fontWeight: 'bold',
                            }}
                          >
                            Eligibility Criteria
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                              verticalAlign: 'center',
                              textAlign: 'center',
                            }}
                          >
                            Any domestic corporation
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                              verticalAlign: 'center',
                              textAlign: 'center',
                            }}
                          >
                            Cooperative corporations engaged in certain
                            activities
                          </TableCell>
                          <TableCell
                            sx={{
                              borderBottom: '1px solid #828282',
                              verticalAlign: 'center',
                              textAlign: 'center',
                            }}
                          >
                            Corporations meeting IRS requirements for S
                            Corporation
                          </TableCell>
                        </TableRow>
                        <TableRow
                          sx={{
                            textAlign: 'center',
                          }}
                        >
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                              verticalAlign: 'center',
                              textAlign: 'center',
                              fontWeight: 'bold',
                            }}
                          >
                            Taxation
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                              verticalAlign: 'cener',
                              textAlign: 'center',
                            }}
                          >
                            Taxed at the corporate level (C Corporation tax)
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                              verticalAlign: 'center',
                              textAlign: 'center',
                            }}
                          >
                            Taxed at the corporate level (C Corporation tax)
                          </TableCell>
                          <TableCell
                            sx={{
                              borderBottom: '1px solid #828282',
                              verticalAlign: 'center',
                              textAlign: 'center',
                            }}
                          >
                            Pass-through taxation; income passed to shareholders
                          </TableCell>
                        </TableRow>
                        <TableRow
                          sx={{
                            textAlign: 'center',
                            backgroundColor: '#f2f2f2',
                          }}
                        >
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                              verticalAlign: 'center',
                              textAlign: 'center',
                              fontWeight: 'bold',
                            }}
                          >
                            Number of Shareholders
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                              verticalAlign: 'center',
                              textAlign: 'center',
                            }}
                          >
                            Can have an unlimited number of shareholders
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                              verticalAlign: 'center',
                              textAlign: 'center',
                            }}
                          >
                            Generally has a limited number of shareholders
                          </TableCell>
                          <TableCell
                            sx={{
                              borderBottom: '1px solid #828282',
                              verticalAlign: 'center',
                              textAlign: 'center',
                            }}
                          >
                            Limited to 100 or fewer shareholders
                          </TableCell>
                        </TableRow>
                        <TableRow
                          sx={{
                            textAlign: 'center',
                          }}
                        >
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                              verticalAlign: 'center',
                              textAlign: 'center',
                              fontWeight: 'bold',
                            }}
                          >
                            Shareholder Types
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                              verticalAlign: 'center',
                              textAlign: 'center',
                            }}
                          >
                            Shareholders can be individuals, other corporations,
                            partnerships, etc.
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                              verticalAlign: 'center',
                              textAlign: 'center',
                            }}
                          >
                            Shareholders are typically cooperative members or
                            patrons
                          </TableCell>
                          <TableCell
                            sx={{
                              borderBottom: '1px solid #828282',
                              verticalAlign: 'center',
                              textAlign: 'center',
                            }}
                          >
                            Shareholders must be U.S. citizens/residents and
                            meet IRS criteria
                          </TableCell>
                        </TableRow>
                        <TableRow
                          sx={{
                            textAlign: 'center',
                            backgroundColor: '#f2f2f2',
                          }}
                        >
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                              verticalAlign: 'center',
                              textAlign: 'center',
                              fontWeight: 'bold',
                            }}
                          >
                            Profit Allocation
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                              verticalAlign: 'center',
                              textAlign: 'center',
                            }}
                          >
                            Profits are taxed at the corporate level; dividends
                            distributed to shareholders are taxed at the
                            individual level
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                              verticalAlign: 'center',
                              textAlign: 'center',
                            }}
                          >
                            Profits taxed at the corporate level; allocated to
                            patrons/members
                          </TableCell>
                          <TableCell
                            sx={{
                              borderBottom: '1px solid #828282',
                              verticalAlign: 'center',
                              textAlign: 'center',
                            }}
                          >
                            Profits are passed through to shareholders and taxed
                            at the individual level
                          </TableCell>
                        </TableRow>
                        <TableRow
                          sx={{
                            textAlign: 'center',
                          }}
                        >
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                              verticalAlign: 'center',
                              textAlign: 'center',
                              fontWeight: 'bold',
                            }}
                          >
                            Deductions
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                              verticalAlign: 'center',
                              textAlign: 'center',
                            }}
                          >
                            Standard business deductions, depreciation, employee
                            salaries, etc.
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                              verticalAlign: 'center',
                              textAlign: 'center',
                            }}
                          >
                            Similar to Form 1120 deductions,
                            cooperative-specific deductions may apply
                          </TableCell>
                          <TableCell
                            sx={{
                              borderBottom: '1px solid #828282',
                              verticalAlign: 'center',
                              textAlign: 'center',
                            }}
                          >
                            Similar to Form 1120 deductions, with certain
                            limitations for S Corporations
                          </TableCell>
                        </TableRow>
                        <TableRow
                          sx={{
                            textAlign: 'center',
                            backgroundColor: '#f2f2f2',
                          }}
                        >
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',

                              verticalAlign: 'center',
                              textAlign: 'center',
                              fontWeight: 'bold',
                            }}
                          >
                            Form Filing
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',

                              verticalAlign: 'center',
                              textAlign: 'center',
                            }}
                          >
                            Filed annually by C Corporations
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',

                              verticalAlign: 'center',
                              textAlign: 'center',
                            }}
                          >
                            Filed by cooperatives engaged in certain activities
                          </TableCell>
                          <TableCell
                            sx={{
                              verticalAlign: 'center',
                              textAlign: 'center',
                            }}
                          >
                            Filed by eligible S Corporations
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  This table compares the significant variations in entity type,
                  taxation, shareholder eligibility, profit allocation,
                  deductions, and filing requirements between Forms 1120,
                  1120-C, and 1120-S. Corporations must understand these
                  distinctions to ensure that they file the correct form and
                  follow IRS regulations.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="what"
                >
                  What is Form 7004?
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  The IRS Form 7004 is an extension form filed by businesses to
                  get an automatic extension of up to 6 months to file certain
                  business income tax. The IRS doesn&apos;t require any
                  signature or a valid reason while filing an extension.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="who"
                >
                  Who can file Form 7004?
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  Most business entities can{' '}
                  <Link
                    href="/tax-extension/efile-irs-form-7004-business-tax-extension-online/"
                    sx={{ color: '#0202ff' }}
                  >
                    file Form 7004
                  </Link>
                  , except for sole proprietors. TaxZerone supports the
                  following businesses that can use{' '}
                  <Link
                    href="/resources/tax-extension/form-7004-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    Form 7004
                  </Link>{' '}
                  to get an extension to file business income tax:
                </Typography>
                <List sx={{ mb: 3 }}>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      C-corps <b>(Form 1120)</b>
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      Cooperative <b>(Form 1120-C)</b>
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      Partnerships{' '}
                      <b>
                        (
                        <Link
                          href="/resources/business-tax/form-1065-instructions/"
                          sx={{ color: '#0202ff' }}
                        >
                          Form 1065
                        </Link>
                        )
                      </b>
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      Multiple-member LLCs as corporations and partnerships
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      Trust, Estates and Others <b>(Form 1041)</b>
                    </ListItemText>
                  </ListItem>
                </List>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 5, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b> Note:</b> The IRS recommends electronic filing over paper
                  filing for its quick and instant status returns.
                </Alert>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="whereform7004"
                >
                  Where to file Form 7004?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  You can file your extension with TaxZerone, in 3 simple steps
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <b> Step 1: </b>Choose the tax year and enter the information
                  requested.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <b> Step 2: </b> Determine the sum payable by providing an
                  estimate of total tax, payments, and credits.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <b> Step 3: </b> Send the return to the IRS.
                </Typography>
                <Box
                  component="div"
                  sx={{
                    mb: 0,
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: '#f9f9f9',
                    backgroundSize: {
                      xs: '20% auto',
                      sm: '20% auto',
                      md: '20% auto',
                    },
                    border: '1px solid #ccc',
                    // color: '#fff',
                  }}
                  // className="wave-bg"
                >
                  <Box sx={{ width: '100%' }}>
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 600,
                        mb: 1,
                        fontSize: '24px !important',
                      }}
                    >
                      Do you want to file an extension for your businesses?
                    </Typography>
                    <Typography variant="body2">
                      E-file Form 7004 with TaxZerone and get an extension for
                      up to 6 months.
                    </Typography>
                  </Box>
                  <Box sx={{ width: '100%', textAlign: 'left', mt: 2 }}>
                    <Button
                      variant="contained"
                      color="primary"
                      sx={{
                        textTransform: 'none',
                        boxShadow: 'none',
                        borderRadius: 2,
                      }}
                      size="large"
                      href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=form1120`}
                    >
                      E-file Form 7004 Now!
                    </Button>
                  </Box>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>
    </AppLayout>
  );
};

export default Form1120;
